'use client';

import type { FC } from 'react';
import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { useTimeLeft } from '@wwe-portal/ui/lib/hooks';

import { BonusBadge } from '../component/bonus-badge';
import { OfferItemGrid } from '../component/offer-item-grid';
import { OfferTimeCountDown } from '../component/offer-time-countdown';
import type { FlagShipCardProps } from '../type';

import { BadgeContent, filterItems } from './common';
import {
  FlagshipMobileCardLayout,
  FlagshipDesktopCardLayout,
} from './flagship-layouts';
import { NotifPip } from './notif-pip';

const FlagshipCountdown: FC<{ endDate: Date }> = ({ endDate }) => {
  const { getTimeText } = useTimeLeft(endDate);
  return <>{getTimeText()}</>;
};

export const FlagshipCard: FC<FlagShipCardProps> = ({
  offer: {
    backgroundImage,
    title,
    description,
    strikethroughPrice,
    price,
    items,
    hasPip,
    quantity,
    maxQuantity,
    bonusPercent,
    badgeIcon,
    hasContest,
    endDate,
    totalItems,
  },
  onClickPrice,
  onClick,
}) => {
  const { isMobile } = useResponsive();
  const filteredItems = filterItems(items);

  if (isMobile) {
    return (
      <FlagshipMobileCardLayout
        background={
          <img
            alt={title ?? 'Offer Background'}
            className="h-full w-full object-cover object-right"
            src={backgroundImage}
          />
        }
        items={
          items ? (
            <div className="absolute bottom-[calc(6000%/259)] left-0 right-0 top-0 flex items-center justify-center stroke-2 text-center text-[19px] font-semibold text-[#F8F8F8]">
              <div className="flex h-[140px] w-[135px] items-center justify-center">
                <OfferItemGrid
                  className="h-full w-full"
                  columnGap={16}
                  imageClassName="h-[calc(5000%/74)]"
                  items={filteredItems}
                  maxCellHeight={72}
                  maxCellWidth={38}
                  quantityClassName="h-[calc(2000%/74)] text-sm"
                  totalItems={totalItems}
                />
              </div>
            </div>
          ) : null
        }
        onClick={onClick}
        overlay={
          <>
            {endDate && (
              <div className="absolute right-3 top-1">
                <OfferTimeCountDown endDate={endDate} />
              </div>
            )}
            {hasPip ? <NotifPip className="size-4" /> : null}
            <BadgeContent badgeIcon={badgeIcon} hasContest={hasContest} />
            {bonusPercent ? (
              <div className="absolute -bottom-1 right-0">
                <BonusBadge
                  className="h-[53px] w-[53px]"
                  percent={bonusPercent}
                />
              </div>
            ) : null}
          </>
        }
        price={
          price != null && (
            <ClickableDiv
              className="flex h-[calc(4200%/259)] items-center justify-center overflow-hidden rounded-b-[6px] border-2 border-solid border-[#D47D01] to-100%"
              data-slot="price"
              onClick={onClickPrice}
              style={{
                background:
                  'linear-gradient(0deg, #FCE983 0%, #E8AE31 12.5%, #E29E1A 51%, #FCE983 56.5%)',
              }}
            >
              <span className="drop-shadow-text px-8 text-center text-lg font-semibold capitalize leading-[27px] text-white">
                {price}
              </span>
            </ClickableDiv>
          )
        }
        quantity={
          quantity != null &&
          maxQuantity != null && (
            <div className="absolute -top-[22px] left-[calc(500%/108)] stroke-2 text-sm font-semibold text-white">
              {quantity}/{maxQuantity}
            </div>
          )
        }
        title={
          <span className="w-full py-[2px] text-center text-sm font-semibold text-[#fffbe3]">
            {title}
          </span>
        }
      />
    );
  }

  return (
    <FlagshipDesktopCardLayout
      backgroundImage={backgroundImage}
      content={
        <>
          <div className="text-yellow800 mt-8 text-sm font-medium">
            Store Offers
          </div>
          <div className="text-text300 mt-2 text-lg font-semibold">{title}</div>
          <div className="text-text400 mt-4 text-sm font-normal">
            {description}
          </div>
          <div className="mt-6 flex flex-col gap-3">
            {strikethroughPrice != null && (
              <div className="bg-text300 flex h-[42px] w-[200px] items-center justify-center rounded-[4px]">
                <span className="px-1 text-center text-sm font-semibold text-[#101010] line-through decoration-[#f81001]">
                  {strikethroughPrice}
                </span>
              </div>
            )}
            <ClickableDiv
              className="bg-yellow800 flex h-[42px] w-[200px] items-center justify-center rounded-[4px]"
              data-slot="price"
              onClick={onClickPrice}
            >
              <span className="px-1 text-center text-base font-semibold leading-6 text-white drop-shadow-[0px_1px_1px_rgba(0,0,0,0.35)]">
                {price}
              </span>
            </ClickableDiv>
          </div>
        </>
      }
      footer={
        <div className="text-text600 ml-10 flex gap-1 text-sm font-semibold">
          {endDate && (
            <>
              <IcoMoonIcon className="text-text700 size-4" iconName="timer" />
              <FlagshipCountdown endDate={endDate} />
            </>
          )}
        </div>
      }
      items={
        <>
          <div className="to-0 absolute inset-0 bg-gradient-to-l from-[rgba(33,37,65,0.3)] from-[30%] to-[#212541]" />
          <div className="absolute inset-0 flex flex-row items-center justify-center">
            <div className="flex h-[240px] w-[calc(26000%/384)] items-center justify-center">
              <OfferItemGrid
                className="h-full w-full"
                columnGap={12}
                imageClassName="h-[calc(8800%/116)]"
                items={filteredItems}
                maxCellHeight={116}
                maxCellWidth={56}
                quantityClassName="h-[calc(2200%/116)] text-[19px]"
                totalItems={totalItems}
              />
            </div>
          </div>
          <BadgeContent
            badgeIcon={badgeIcon}
            hasContest={hasContest}
            className="absolute left-6 top-6"
          />
          {quantity != null && maxQuantity != null && (
            <div className="absolute bottom-6 left-6 stroke-2 text-sm font-semibold text-white">
              {quantity}/{maxQuantity}
            </div>
          )}
          {bonusPercent ? (
            <div className="absolute bottom-2 right-2 h-[53px] w-[53px]">
              <BonusBadge
                className="absolute h-full w-full"
                percent={bonusPercent}
              />
            </div>
          ) : null}
        </>
      }
      onClick={onClick}
      overlay={null}
    />
  );
};

export const FlagshipMobileCard = FlagshipCard;
export const FlagshipDesktopCard = FlagshipCard;
